import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import {
  ApiEnvelope,
  AdminContractSearchData,
  AdminContractSearchParams,
  AdminContractDetailData,
  AdminRequestDetailData,
  AdminNoteRequest,
  AdminNoteResponseData,
  AdminLoginResponseData,
  AdminLoginConfig,
  AdminUserListData,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  AdminUserResponseData,
  AdminUserDeleteData,
  BackendFeatureFlagListData,
  BackendFeatureFlagRecord,
  BackendFeatureFlagUpdate,
  ConfirmationDocumentInfo,
} from '@core/models';

/**
 * Service for admin/support operations
 */
@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly api = inject(ApiService);

  /**
   * Public login mode for the admin login page (no JWT required).
   */
  getLoginConfig(): Observable<ApiEnvelope<AdminLoginConfig>> {
    return this.api.get<AdminLoginConfig>('/admin/auth/login-config');
  }

  /**
   * Dev login with email and password (non-production only).
   */
  devLogin(credentials: {
    email: string;
    password: string;
  }): Observable<ApiEnvelope<AdminLoginResponseData>> {
    return this.api.post<AdminLoginResponseData>('/admin/auth/dev-login', credentials);
  }

  /**
   * Stub SSO callback (not yet implemented on the backend).
   */
  ssoCallback(): Observable<ApiEnvelope<AdminLoginResponseData>> {
    return this.api.post<AdminLoginResponseData>('/admin/auth/sso-callback', {});
  }

  /**
   * GET /admin/feature-flags — backend feature flags (boolean + string).
   */
  listFeatureFlags(): Observable<ApiEnvelope<BackendFeatureFlagListData>> {
    return this.api.get<BackendFeatureFlagListData>('/admin/feature-flags');
  }

  /**
   * PATCH /admin/feature-flags/:key — update enabled or value.
   */
  updateFeatureFlag(
    key: string,
    update: BackendFeatureFlagUpdate,
  ): Observable<ApiEnvelope<BackendFeatureFlagRecord>> {
    return this.api.patch<BackendFeatureFlagRecord>(`/admin/feature-flags/${key}`, update);
  }

  /**
   * List or search VIN-add submissions (paginated, sortable).
   * Empty search returns the most recent submissions across all contracts.
   */
  searchContracts(
    params: AdminContractSearchParams = {},
  ): Observable<ApiEnvelope<AdminContractSearchData>> {
    const queryParams = new URLSearchParams();
    const search = params.search?.trim();
    if (search) {
      queryParams.set('search', search);
    }
    if (params.page != null) {
      queryParams.set('page', String(params.page));
    }
    if (params.limit != null) {
      queryParams.set('limit', String(params.limit));
    }
    if (params.sort) {
      queryParams.set('sort', params.sort);
    }
    if (params.order) {
      queryParams.set('order', params.order);
    }

    const qs = queryParams.toString();
    return this.api.get<AdminContractSearchData>(
      qs ? `/admin/contracts?${qs}` : '/admin/contracts',
    );
  }

  /**
   * Get detailed information about a contract including its requests
   * @param contractContextId Contract context ID
   * @returns Observable with contract details and request list
   */
  getContractDetail(contractContextId: string): Observable<ApiEnvelope<AdminContractDetailData>> {
    return this.api.get<AdminContractDetailData>(`/admin/contracts/${contractContextId}`);
  }

  /**
   * Get detailed information about a request
   * @param requestId Request ID
   * @returns Observable with request details including audit trail
   */
  getRequestDetail(requestId: string): Observable<ApiEnvelope<AdminRequestDetailData>> {
    return this.api.get<AdminRequestDetailData>(`/admin/requests/${requestId}`);
  }

  /**
   * Add a note to a request
   * @param requestId Request ID
   * @param note Note content
   * @returns Observable with note save confirmation
   */
  addNote(requestId: string, note: string): Observable<ApiEnvelope<AdminNoteResponseData>> {
    const request: AdminNoteRequest = { note };
    return this.api.post<AdminNoteResponseData>(`/admin/requests/${requestId}/note`, request);
  }

  /**
   * GET /admin/requests/:requestId/document — confirmation document status.
   */
  getRequestDocument(requestId: string): Observable<ApiEnvelope<ConfirmationDocumentInfo>> {
    return this.api.get<ConfirmationDocumentInfo>(`/admin/requests/${requestId}/document`);
  }

  /**
   * POST /admin/requests/:requestId/document/generate — create/regenerate PDF.
   */
  generateRequestDocument(requestId: string): Observable<ApiEnvelope<ConfirmationDocumentInfo>> {
    return this.api.post<ConfirmationDocumentInfo>(
      `/admin/requests/${requestId}/document/generate`,
      {},
    );
  }

  /**
   * GET /admin/requests/:requestId/document/pdf — download confirmation PDF.
   */
  downloadRequestDocumentPdf(requestId: string): Observable<Blob> {
    return this.api.getBlob(`/admin/requests/${requestId}/document/pdf`);
  }

  /**
   * GET /admin/users — admin user roster + stat summary (ops-users-001).
   */
  getUsers(): Observable<ApiEnvelope<AdminUserListData>> {
    return this.api.get<AdminUserListData>('/admin/users');
  }

  /**
   * POST /admin/users — create a new admin user.
   */
  createUser(body: AdminUserCreateRequest): Observable<ApiEnvelope<AdminUserResponseData>> {
    return this.api.post<AdminUserResponseData>('/admin/users', body);
  }

  /**
   * PUT /admin/users/:id — update an existing admin user.
   */
  updateUser(
    userId: string,
    body: AdminUserUpdateRequest,
  ): Observable<ApiEnvelope<AdminUserResponseData>> {
    return this.api.put<AdminUserResponseData>(`/admin/users/${userId}`, body);
  }

  /**
   * DELETE /admin/users/:id — remove an admin user.
   */
  deleteUser(userId: string): Observable<ApiEnvelope<AdminUserDeleteData>> {
    return this.api.delete<AdminUserDeleteData>(`/admin/users/${userId}`);
  }
}
